import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTeam } from '../context/TeamContext';
import { useAuth } from '../context/AuthContext';

function TeamMembersPage() {
  const { teamMembers, deleteTeamMember, removeAllUnavailableEvents } = useTeam();
  const { loading: authLoading } = useAuth();
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDateRemoveConfirm, setShowDateRemoveConfirm] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  const qualificationOrder = ['JimmyJib', 'Moving', 'Switcher', 'Title', 'Animasi', 'Foto', 'Lighting', 'Socmed'];
  const eventOrder = ['IBR1', 'IBR2', 'IBR3', 'IBR4', 'NOW', 'WBI', 'Wedding', 'Special Events'];

  // Calculate member statistics
  const activeMembers = teamMembers ? teamMembers.filter(member => member.active) : [];
  const socmedMembers = teamMembers ? teamMembers.filter(member => member.qualifications?.Socmed) : [];

  const filteredMembers = useMemo(() => {
    if (!teamMembers) return [];
    return teamMembers.filter(member => {
      const nameMatch = member.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      switch (filter) {
        case 'inactive':
          return nameMatch && !member.active;
        case 'active':
          return nameMatch && member.active;
        case 'socmed':
          return nameMatch && member.qualifications.Socmed;
        default:
          return nameMatch;
      }
    });
  }, [teamMembers, searchTerm, filter]);

  const handleDelete = (member) => {
    setMemberToDelete(member);
  };

  const confirmDelete = () => {
    if (memberToDelete) {
      deleteTeamMember(memberToDelete.id);
      setMemberToDelete(null);
    }
  };

  const handleEdit = (member) => {
    navigate(`/edit-member/${member.id}`);
  };

  const handleRemoveDates = () => {
    setShowDateRemoveConfirm(true);
  };

  const confirmRemoveDates = () => {
    if (typeof removeAllUnavailableEvents === 'function') {
      removeAllUnavailableEvents();
    } else {
      console.error("removeAllUnavailableEvents is not a function");
    }
    setShowDateRemoveConfirm(false);
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };

  const getPreferredEvents = (eventPreferences) => {
    if (!eventPreferences) return 'None';
    return eventOrder
      .filter(event => eventPreferences[event])
      .join(', ') || 'None';
  };

  const getQualifications = (qualifications) => {
    if (!qualifications) return 'None';
    return qualificationOrder
      .filter(qual => qualifications[qual])
      .join(', ') || 'None';
  };

  const getUnavailableEventsString = (unavailableDates) => {
    if (!unavailableDates || Object.keys(unavailableDates).length === 0) return [];
    return Object.entries(unavailableDates).map(([date, events]) => {
      const unavailableEvents = eventOrder
        .filter(event => events[event])
        .join(', ');
      return unavailableEvents ? `${date}: ${unavailableEvents}` : null;
    }).filter(Boolean);
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1 className="text-3xl font-bold text-gray-800 text-center mb-8">TEAM MEMBERS</h1>
      
      {/* Header section with member count and action buttons */}
      <div className="flex justify-between items-start mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="space-y-2">
          <div className="text-lg font-semibold text-gray-800">
            Total Registered Members: {teamMembers ? teamMembers.length : 0}
          </div>
          <div className="text-base font-semibold text-green-600">
            Active Members: {activeMembers.length}
          </div>
          <div className="text-base font-semibold text-blue-600">
            Socmed Members: {socmedMembers.length}
          </div>
        </div>
        <div className="flex gap-4">
          <div className="relative">
            <button 
              onClick={handleRemoveDates} 
              className="bg-yellow-500 text-black border-0 px-5 py-2.5 rounded cursor-pointer text-base font-bold transition-colors duration-300 hover:bg-yellow-600"
            >
              Remove Unavailable Events
            </button>
            {showDateRemoveConfirm && (
              <div className="absolute top-full right-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-4 z-10 w-80">
                <p className="text-gray-700 mb-3">
                  Are you sure you want to remove all unavailable events for all members?
                </p>
                <div className="flex gap-2">
                  <button 
                    onClick={confirmRemoveDates} 
                    className="bg-red-500 text-white border-0 px-3 py-1 rounded cursor-pointer text-sm font-medium transition-colors duration-300 hover:bg-red-600"
                  >
                    Yes, Remove
                  </button>
                  <button 
                    onClick={() => setShowDateRemoveConfirm(false)} 
                    className="bg-gray-500 text-white border-0 px-3 py-1 rounded cursor-pointer text-sm font-medium transition-colors duration-300 hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
          <Link to="/add-member" className="bg-green-500 text-white border-0 px-5 py-2.5 rounded cursor-pointer text-base font-bold transition-colors duration-300 hover:bg-green-600 no-underline">
            ADD MEMBER
          </Link>
        </div>
      </div>

      {/* Search and filter section */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search members..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-2.5 py-2.5 mb-4 border border-gray-300 rounded text-base focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          />
        </div>
        <div className="flex gap-2">
          <button 
            className={`px-4 py-2 rounded text-sm font-medium border transition-all duration-200 ${
              filter === 'all' 
                ? 'bg-blue-500 text-white border-blue-500' 
                : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
            }`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button 
            className={`px-4 py-2 rounded text-sm font-medium border transition-all duration-200 ${
              filter === 'active' 
                ? 'bg-blue-500 text-white border-blue-500' 
                : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
            }`}
            onClick={() => setFilter('active')}
          >
            Active
          </button>
          <button 
            className={`px-4 py-2 rounded text-sm font-medium border transition-all duration-200 ${
              filter === 'inactive' 
                ? 'bg-blue-500 text-white border-blue-500' 
                : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
            }`}
            onClick={() => setFilter('inactive')}
          >
            Inactive
          </button>
          <button 
            className={`px-4 py-2 rounded text-sm font-medium border transition-all duration-200 ${
              filter === 'socmed' 
                ? 'bg-blue-500 text-white border-blue-500' 
                : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
            }`}
            onClick={() => setFilter('socmed')}
          >
            Socmed
          </button>
        </div>
      </div>

      {/* Members list */}
      {filteredMembers.length > 0 ? (
        <ul className="space-y-4">
          {filteredMembers.map((member) => (
            <li 
              key={member.id} 
              className={`bg-white border-2 rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow ${
                !member.active ? 'border-red-200' : 'border-green-200'
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{member.name}</h2>
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center">
                      <span className="font-bold text-gray-700 w-32 text-base">Qualifications:</span>
                      <span className="text-blue-600 font-medium text-base">{getQualifications(member.qualifications)}</span>
                    </div>
                    <div className="flex flex-wrap items-center">
                      <span className="font-bold text-gray-700 w-32 text-base">Events:</span>
                      <span className="text-blue-600 font-medium text-base">{getPreferredEvents(member.eventPreferences)}</span>
                    </div>
                    <div className="flex flex-wrap items-center">
                      <span className="font-bold text-gray-700 w-32 text-base">Status:</span>
                      <span className={`px-3 py-1 rounded-full text-base font-bold ${
                        member.active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {member.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-start">
                      <span className="font-bold text-gray-700 w-32 text-base">Unavailable:</span>
                      <div className="text-blue-600 font-medium text-base">
                        {(() => {
                          const unavailableList = getUnavailableEventsString(member.unavailableDates);
                          if (unavailableList.length > 0) {
                            return (
                              <div className="space-y-1">
                                {unavailableList.map((dateEvent, index) => (
                                  <div key={index}>{dateEvent}</div>
                                ))}
                              </div>
                            );
                          }
                          return 'None';
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button 
                    onClick={() => handleEdit(member)} 
                    className="bg-blue-500 text-white border-0 px-4 py-2 rounded cursor-pointer text-sm font-medium transition-colors duration-300 hover:bg-blue-600"
                  >
                    Edit
                  </button>
                  <div className="relative">
                    <button 
                      onClick={() => handleDelete(member)} 
                      className="bg-red-500 text-white border-0 px-4 py-2 rounded cursor-pointer text-sm font-medium transition-colors duration-300 hover:bg-red-600"
                    >
                      Delete
                    </button>
                    {memberToDelete && memberToDelete.id === member.id && (
                      <div className="absolute top-full right-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-4 z-10 w-64">
                        <p className="text-gray-700 mb-3">
                          Are you sure you want to delete {member.name}?
                        </p>
                        <div className="flex gap-2">
                          <button 
                            onClick={confirmDelete} 
                            className="bg-red-500 text-white border-0 px-3 py-1 rounded cursor-pointer text-sm font-medium transition-colors duration-300 hover:bg-red-600"
                          >
                            Yes, Delete
                          </button>
                          <button 
                            onClick={() => setMemberToDelete(null)} 
                            className="bg-gray-500 text-white border-0 px-3 py-1 rounded cursor-pointer text-sm font-medium transition-colors duration-300 hover:bg-gray-600"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">
            {searchTerm ? "No matching members found." : "No team members added yet."}
          </p>
        </div>
      )}

      {/* Success message modal */}
      {showSuccessMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <p className="text-green-700 text-center">
              All unavailable events have been removed successfully!
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamMembersPage;